import React, { useEffect, useRef, useState } from 'react';
import { View, Image, StyleSheet, Animated, Easing, Platform, ActivityIndicator } from 'react-native';
import { brand } from '../design-system/theme/tokens';

/**
 * Web Splash Screen Component
 * Shows an animated loading screen with the logo while the app boots.
 * Only displays on web platform.
 *
 * The splash is driven by auth resolution, not a fixed timer: it hides as soon
 * as `ready` is true (auth/billing resolved) past a short MIN_MS floor so it
 * never flashes, with a MAX_MS hard fallback so a hung network can't trap the
 * user on the splash forever.
 */
const MIN_MS = 600;
const MAX_MS = 4000;

export default function WebSplashScreen({
  onFinish,
  ready = false,
}: {
  onFinish: () => void;
  ready?: boolean;
}) {
  const [fadeAnim] = useState(new Animated.Value(0));
  const [scaleAnim] = useState(new Animated.Value(0.8));
  const onFinishRef = useRef(onFinish);
  const finishedRef = useRef(false);
  const minElapsedRef = useRef(false);
  const readyRef = useRef(ready);

  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  const beginFadeOut = React.useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 400,
      useNativeDriver: false,
    }).start(() => onFinishRef.current());
  }, [fadeAnim]);

  // Hide as soon as auth resolves (once the minimum display time has passed).
  useEffect(() => {
    readyRef.current = ready;
    if (ready && minElapsedRef.current) {
      beginFadeOut();
    }
  }, [ready, beginFadeOut]);

  useEffect(() => {
    // Only show splash screen on web
    if (Platform.OS !== 'web') {
      onFinishRef.current();
      return;
    }

    // Fade in and scale animation
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 600,
        easing: Easing.out(Easing.back(1.5)),
        useNativeDriver: false,
      }),
    ]).start();

    const minTimer = setTimeout(() => {
      minElapsedRef.current = true;
      if (readyRef.current) beginFadeOut();
    }, MIN_MS);
    // Hard fallback: never trap the user on the splash if auth never resolves.
    const maxTimer = setTimeout(beginFadeOut, MAX_MS);

    return () => {
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
  }, [fadeAnim, scaleAnim, beginFadeOut]);

  // Don't render on native platforms
  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
        },
      ]}
    >
      <Animated.View
        style={{
          transform: [{ scale: scaleAnim }],
        }}
      >
        <Image
          source={require('../../assets/icon-web.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </Animated.View>

      {/* Optional: Loading spinner */}
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={brand.primary} />
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: 40,
  },
  loaderContainer: {
    marginTop: 20,
  },
});
